import { useMemo, useState } from 'react';
import { ServiceBooking, ServiceOrder } from '@turizi/shared';
import { IconCalendar, IconClock } from '@tabler/icons-react';
import { formatDayLong } from '@/lib/format-utils';
import { ShowPaxGroup } from '@/components/snippets';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { calculateUniquePaxCategories } from '@/lib/models-utils';
import SelectTimePopover from '@/components/select-time-popover';
import { useUpdateServiceBooking } from '@/hooks/api-hooks';
import { useTranslation } from 'react-i18next';
import { TagIcon } from '@/lib/tag-utils';

interface Props {
    serviceOrder: ServiceOrder;
    className?: string;
    editable?: boolean;
}

export default function ServiceOrderBookingsSummary({ serviceOrder, className, editable = true }: Props) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const { mutateAsync: updateServiceBooking } = useUpdateServiceBooking();

    const bookings = useMemo(() => {
        const linkedBookings = serviceOrder?.service_order_bookings
            ?.filter(link => link.service_booking?.reservation_id !== null)
            ?.map(link => link?.service_booking)
            .filter(Boolean) as ServiceBooking[] || [];

        const map = new Map<number, ServiceBooking>();
        [...linkedBookings].forEach(b => {
            if (b) {
                const key = b.id ?? Math.random();
                if (!map.has(key)) map.set(key, b);
            }
        });

        return Array.from(map.values()).sort((a, b) => {
            const dayA = a?.start_day || '9999-99-99';
            const dayB = b?.start_day || '9999-99-99';
            const dayCompare = dayA.localeCompare(dayB);
            if (dayCompare !== 0) return dayCompare;

            const hourA = a?.start_hour || '99:99';
            const hourB = b?.start_hour || '99:99';
            return hourA.localeCompare(hourB);
        });
    }, [serviceOrder]);

    // this number seems wrong
    const uniquePaxCategories = useMemo(() => {
        return calculateUniquePaxCategories(serviceOrder);
    }, [serviceOrder]);

    const firstBooking = bookings?.[0];

    const uniqueServices = useMemo(() => {
        const map = new Map<number, ServiceBooking['service_data']>();
        bookings.forEach(b => {
            if (b.service_data && !map.has(b.service_id)) {
                map.set(b.service_id, b.service_data);
            }
        });
        return Array.from(map.values());
    }, [bookings]);

    const handleStartHourChange = async (value: string | null) => {
        if (!bookings || bookings.length === 0) return;
        setSaving(true);
        try {
            await Promise.all(
                bookings.map((booking) =>
                    updateServiceBooking({
                        id: booking.id,
                        start_hour: value ?? '',
                    })
                )
            );
        } catch (error) {
            console.error('Failed to update start_hour for bookings in service order:', error);
        } finally {
            setSaving(false);
        }
    };

    if (!firstBooking) return null;

    return (
        <div className={className || 'text-xs text-gray-600 font-medium flex flex-col md:flex-row md:items-center gap-2 w-full'}>
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 solid-box-border">
                    <IconCalendar size={14} color="gray" stroke={1} />
                    {formatDayLong(firstBooking?.start_day || '')}
                </div>
                <div className="flex items-center gap-1 solid-box-border">
                    <SelectTimePopover
                        size="xs"
                        label={t('start_hour')}
                        value={firstBooking?.start_hour || ''}
                        onChange={handleStartHourChange}
                        loading={saving}
                        disabled={!editable}
                    />
                </div>
                <ShowPaxGroup
                    adults={uniquePaxCategories?.adults || 0}
                    guests={uniquePaxCategories?.guests || 0}
                    seniors={uniquePaxCategories?.seniors || 0}
                    children={uniquePaxCategories?.children || 0}
                    infants={uniquePaxCategories?.infants || 0}
                />
            </div>
            <div>
                {uniqueServices.map(service => (
                    <div key={service.id} className='text-xxs  text-gray-700 p-1 flex gap-1 items-center'>
                        <TagIcon name={service.category} noBackground hideText size='xxs' />
                        {ServiceTitleStatic(service)}
                    </div>
                ))}
            </div>
        </div>
    );
}
